import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  CalendarDays,
  DoorClosed,
  Gavel,
  Hash,
  Mic,
  MicOff,
  Scale,
  Volume2,
  VolumeX,
  Users,
  Video,
  VideoOff,
} from "lucide-react";
import Button from "../../../components/ui/Button";
import { useAuth } from "../../../context/AuthProvider";
import { getMeetingMedia, setTrackEnabled } from "../../../utils/media";
import toast from "react-hot-toast";
import { getPublicPrejoinCaseInfoApi } from "../../../api/publicMeetingJoinApi";
import DevicePicker from "../../../components/common/DevicePicker";
import { usePublicMeetingStore } from "../../../store/public.meeting.store";
import { useMeetingStore } from "../../../store/meeting.store";
import {
  ADVOCATE_PREJOIN_PAYLOAD_KEY,
  PUBLIC_ADVOCATE_POPUP_KEY,
  readStoredPayload,
} from "../../../utils/advocateLobbyJoin";

export interface PrejoinCaseInfo {
  highCourtName: string;
  benchName: string;
  courtRoom: string;
  caseNumber: any;
  cnr: any;
  caseTitle?: string;
  caseType?: string;
  hearingDate?: string;
  judges?: string[];
  caseStage?: string;
  status?: string;
  benchRoomInfo?: any;
  benchRoomId?: string;
  courtRoomInfo?: any;
  courtRoomId?: string;
}

interface PrejoinRouteState {
  meetingId?: string;
  caseNumber?: string;
  user?: any;
  navigateFrom?: string;
  cases?: PrejoinCaseInfo[];
}

const initials = (name?: string) =>
  (name || "User")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");

const PrejoinMeeting = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, setUser } = useAuth();
  const setCaseItem = usePublicMeetingStore((state) => state.setCaseItem);
  const todaysSchedule = usePublicMeetingStore((state) => state.todaysSchedule);

  const setTodaysSchedule = usePublicMeetingStore(
    (state) => state.setTodaysSchedule,
  );
  const setMeetingState = usePublicMeetingStore(
    (state) => state.setMeetingState,
  );
  const setLocalMicOn = useMeetingStore((state) => state.setLocalMicOn);

  const storedState = useRef(
    readStoredPayload<PrejoinRouteState>(ADVOCATE_PREJOIN_PAYLOAD_KEY),
  );
  const state = (location.state ||
    storedState.current ||
    {}) as PrejoinRouteState;
  useEffect(() => {
    if (!state.caseNumber) {
      const isPublicAdvocatePopup =
        window.sessionStorage.getItem(PUBLIC_ADVOCATE_POPUP_KEY) === "1";
      if (isPublicAdvocatePopup) {
        window.sessionStorage.removeItem(PUBLIC_ADVOCATE_POPUP_KEY);
        window.close();
        window.setTimeout(() => navigate("/", { replace: true }), 250);
        return;
      }

      navigate("/join-session");
    } else {
      setUser(state.user);
    }
  }, [state]);

  useEffect(() => {
    if (
      state?.navigateFrom === "public-meeting" &&
      (state?.user?.role === "Advocate" || user?.role === "Advocate")
    ) {
      window.sessionStorage.setItem(PUBLIC_ADVOCATE_POPUP_KEY, "1");
    }
  }, [state?.navigateFrom, state?.user?.role, user?.role]);

  const [caseInfo, setCaseInfo] = useState<PrejoinCaseInfo>({
    highCourtName: "",
    benchName: "",
    courtRoom: "",
    caseNumber: state.caseNumber,
    cnr: state.caseNumber,
  });

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const [camOn, setCamOn] = useState(true);
  const [micOn, setMicOn] = useState(true);
  const [mediaError, setMediaError] = useState("");
  const [cameraId, setCameraId] = useState<string>("");
  const [microphoneId, setMicrophoneId] = useState<string>("");
  const [speakerId, setSpeakerId] = useState<string>("");
  const [hasCameraPermission, setHasCameraPermission] = useState(true);
  const [hasMicPermission, setHasMicPermission] = useState(true);
  const [speakerOn, setSpeakerOn] = useState(true);
  const [speakerTesting, setSpeakerTesting] = useState(false);

  const stopStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
  }, []);
  useEffect(() => {
    setMediaError("");
  }, []);

  const attachPreviewStream = useCallback((stream: MediaStream) => {
    if (!videoRef.current) return;

    videoRef.current.srcObject = null;
    videoRef.current.srcObject = stream;
    void videoRef.current.play().catch(() => undefined);
  }, []);

  // Acquire a stream with the chosen devices and attach it to the preview.
  const acquireStream = useCallback(
    async (nextCameraId?: string, nextMicId?: string) => {
      try {
        stopStream();
        const videoConstraint = nextCameraId
          ? { deviceId: { exact: nextCameraId } }
          : true;
        const audioConstraint = nextMicId
          ? { deviceId: { exact: nextMicId } }
          : true;

        let stream: MediaStream;

        try {
          stream = await getMeetingMedia({
            video: videoConstraint,
            audio: audioConstraint,
          });
          setHasCameraPermission(stream.getVideoTracks().length > 0);
          setHasMicPermission(stream.getAudioTracks().length > 0);
        } catch (err) {
          stream = await getMeetingMedia({
            video: videoConstraint,
            audio: false,
          });
          setHasCameraPermission(stream.getVideoTracks().length > 0);
          setHasMicPermission(false);
          setMicOn(false);
          setLocalMicOn(false);
        }

        streamRef.current = stream;
        setTrackEnabled(stream, "audio", micOn);
        setTrackEnabled(stream, "video", camOn);
        attachPreviewStream(stream);

        // Record the actual devices in use so the pickers reflect reality.
        const camTrack = stream.getVideoTracks()[0];
        const micTrack = stream.getAudioTracks()[0];
        if (camTrack) setCameraId(camTrack.getSettings().deviceId || "");
        if (micTrack) setMicrophoneId(micTrack.getSettings().deviceId || "");
        setMediaError(
          micTrack
            ? ""
            : "Microphone permission is disabled. Enable microphone permission before joining.",
        );
      } catch (err) {
        console.error("getUserMedia failed", err);
        setHasCameraPermission(false);
        setHasMicPermission(false);
        setCamOn(false);
        setMicOn(false);
        setLocalMicOn(false);
        setMediaError("Camera permission denied or device unavailable.");
      }
    },
    [attachPreviewStream, camOn, micOn, stopStream],
  );

  // Swap a single track in place. Keeps the other track (and MediaStream object)
  // alive so the <video> element never re-attaches → no flicker.
  const swapTrack = useCallback(
    async (kind: "audio" | "video", deviceId: string) => {
      const stream = streamRef.current;
      if (!stream) {
        // No stream yet — fall back to a full acquire.
        void acquireStream(
          kind === "video" ? deviceId : cameraId,
          kind === "audio" ? deviceId : microphoneId,
        );
        return;
      }

      try {
        const tmp = await getMeetingMedia(
          kind === "audio"
            ? { audio: { deviceId: { exact: deviceId } }, video: false }
            : { video: { deviceId: { exact: deviceId } }, audio: false },
        );
        const newTrack = tmp.getTracks()[0];
        if (!newTrack) return;

        const oldTrack =
          kind === "audio"
            ? stream.getAudioTracks()[0]
            : stream.getVideoTracks()[0];
        if (oldTrack) {
          stream.removeTrack(oldTrack);
          oldTrack.stop();
        }
        newTrack.enabled = kind === "audio" ? micOn : true;

        if (kind === "video") {
          const updatedStream = new MediaStream([
            ...stream.getAudioTracks(),
            newTrack,
          ]);
          streamRef.current = updatedStream;
          setCamOn(true);
          setHasCameraPermission(true);
          setCameraId(newTrack.getSettings().deviceId || deviceId);
          attachPreviewStream(updatedStream);
        } else {
          stream.addTrack(newTrack);
        }

        setMediaError("");
      } catch (err) {
        console.error("track swap failed", err);
        if (kind === "audio") {
          setHasMicPermission(false);
          setMicOn(false);
          setLocalMicOn(false);
          setMediaError(
            "Microphone permission is disabled. Enable microphone permission before joining.",
          );
        } else {
          setHasCameraPermission(false);
          setCamOn(false);
          setMediaError("Camera permission denied or device unavailable.");
        }
      }
    },
    [acquireStream, attachPreviewStream, micOn, cameraId, microphoneId],
  );

  const selectCamera = (device: MediaDeviceInfo) => {
    setCameraId(device.deviceId);
    setCamOn(true);
    void swapTrack("video", device.deviceId);
  };

  const selectMicrophone = (device: MediaDeviceInfo) => {
    setMicrophoneId(device.deviceId);
    void swapTrack("audio", device.deviceId);
  };

  const selectSpeaker = (device: MediaDeviceInfo) => {
    setSpeakerId(device.deviceId);
  };

  const handleSpeakerTest = () => {
    if (speakerTesting) return;
    if (!speakerOn) {
      toast.error("Speaker is turned off.");
      return;
    }

    try {
      setSpeakerTesting(true);
      const AudioContextClass =
        window.AudioContext || (window as any).webkitAudioContext;
      const audioContext = new AudioContextClass();
      const oscillator = audioContext.createOscillator();
      const gain = audioContext.createGain();

      oscillator.type = "sine";
      oscillator.frequency.value = 660;
      gain.gain.setValueAtTime(0.001, audioContext.currentTime);
      gain.gain.exponentialRampToValueAtTime(
        0.2,
        audioContext.currentTime + 0.03,
      );
      gain.gain.exponentialRampToValueAtTime(
        0.001,
        audioContext.currentTime + 0.7,
      );

      oscillator.connect(gain);

      if (speakerId && "setSinkId" in HTMLMediaElement.prototype) {
        const destination = audioContext.createMediaStreamDestination();
        const audio = new Audio();
        audio.srcObject = destination.stream;
        (audio as any).setSinkId(speakerId).catch(() => {
          toast.error("Selected speaker is unavailable.");
        });
        void audio.play();
        gain.connect(destination);
        oscillator.onended = () => {
          audio.pause();
          audio.srcObject = null;
          audioContext.close();
          setSpeakerTesting(false);
        };
      } else {
        gain.connect(audioContext.destination);
        oscillator.onended = () => {
          audioContext.close();
          setSpeakerTesting(false);
        };
      }

      oscillator.start();
      oscillator.stop(audioContext.currentTime + 0.75);
    } catch (error) {
      setSpeakerTesting(false);
      toast.error("Speaker test failed.");
    }
  };

  const handleCaseInfo = async (caseNumber?: any) => {
    const res = await getPublicPrejoinCaseInfoApi({
      caseNo: caseNumber,
      enrollment: user?.enrollment,
    });
    const resp = res?.data;
    if (resp.status) {
      setTodaysSchedule(resp.cases);
      setCaseInfo({
        ...caseInfo,
        caseTitle: resp.data.caseTitle,
        caseType: resp.data.caseType,
        hearingDate: resp.data.hearingDate,
        judges: resp.data.judges,
        caseStage: resp.data.caseStage,
        caseNumber: resp.data.cnr,
        cnr: resp.data.cnr,
        highCourtName:
          resp.data.highcourt.highCourtName ||
          resp.data.courtRoomInfo.courtRoomName,
        benchRoomInfo: resp.data.benchRoomInfo.benchName,
        courtRoomInfo:
          resp.data.benchRoomInfo.courtRoom ||
          resp.data.courtRoomInfo.courtRoomNo,
        status: resp.data.status,
        courtRoomId: resp.data.courtRoomId,
        courtRoom: resp.data.courtRoom,
      });
    }
  };

  useEffect(() => {
    handleCaseInfo(state?.caseNumber);
    void acquireStream();
    return () => stopStream();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.caseNumber, user?.enrollment]);

  const handleJoin = () => {
    if (!hasCameraPermission) {
      return toast.error("Please enable camera permission.");
    }
    if (!hasMicPermission) {
      return toast.error("Please enable microphone permission.");
    }
    if (!camOn) return toast.error("Please turn on camera.");
    setMicOn(false);
    setTrackEnabled(streamRef.current, "audio", false);
    setLocalMicOn(false);
    // navigate("/advocate-lobby", {
    //   state: { cases: [caseInfo], case: caseInfo },
    // });
    if (state?.navigateFrom === "advocate-dashboard") {
      setCaseItem(caseInfo);
      stopStream();
      navigate("/advocate-lobby?fullscreen=1", {
        state: {
          cases: todaysSchedule?.length ? todaysSchedule : state.cases,
          case: caseInfo,
          navigateFrom: "advocate-dashboard",
        },
      });
      return;
    }
    if (
      state?.navigateFrom === "public-meeting" &&
      (state?.user?.role === "Advocate" || user?.role === "Advocate")
    ) {
      setMeetingState("lobby");
      setCaseItem(caseInfo);
      setUser(state?.user);
      stopStream();
      navigate("/meeting?fullscreen=1", {
        replace: true,
        state: {
          cases: todaysSchedule?.length ? todaysSchedule : [caseInfo],
          case: caseInfo,
          navigateFrom: "public-meeting",
        },
      });
      return;
    }
    setMeetingState("lobby");
    setCaseItem(caseInfo);
    setUser(state?.user);
    stopStream();
    navigate("/meeting?fullscreen=1", {
      replace: true,
      state: {
        cases: todaysSchedule?.length ? todaysSchedule : [caseInfo],
        case: caseInfo,
        navigateFrom: state?.navigateFrom || "public-meeting",
      },
    });
  };
  const handleCancel = () => {
    stopStream();
    if (window.opener && !window.opener.closed) {
      window.close();
      window.setTimeout(() => navigate("/"), 250);
      return;
    }
    navigate("/");
  };

  return (
    <div className="p-3">
      <div className="flex min-h-[calc(100vh-30px)] items-center justify-center rounded-2xl bg-white px-4 py-8 sm:px-6">
        <div className="w-full max-w-5xl">
          <div className="mb-6 text-center">
            <h1 className="text-2xl font-bold text-slate-900 sm:text-3xl">
              {caseInfo.caseTitle || "Court Hearing"}
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              {caseInfo.highCourtName} · {caseInfo.courtRoom}
            </p>
          </div>

          <div className="grid grid-cols-1 items-center gap-6 lg:grid-cols-[1.22fr_0.9fr]">
            <div>
              <div className="overflow-hidden rounded-2xl bg-slate-900 shadow-xl ring-1 ring-slate-200">
                <div className="aspect-video w-full">
                  <video
                    ref={videoRef}
                    autoPlay
                    muted
                    playsInline
                    className={`h-full w-full object-cover ${camOn ? "" : "hidden"}`}
                    style={{ transform: "scaleX(-1)" }}
                  />

                  {!camOn && (
                    <div className="flex h-full w-full flex-col items-center justify-center gap-3 text-slate-300">
                      <div className="flex h-20 w-20 items-center justify-center rounded-full bg-slate-700 text-2xl font-bold text-white">
                        {initials(user?.name)}
                      </div>
                      <p className="text-sm font-medium">
                        Your camera is turned off
                      </p>
                    </div>
                  )}
                </div>

                <div className="flex flex-col gap-4 border-t border-white/10 bg-slate-800 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    {user?.name && (
                      <p className="text-sm font-semibold text-white">
                        {user.name}
                      </p>
                    )}
                    {user?.role && (
                      <p className="text-xs text-slate-400">{user.role}</p>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center justify-center gap-3 sm:justify-end">
                    <div className="flex items-center">
                      <button
                        type="button"
                        onClick={() => {
                          if (!hasMicPermission) {
                            toast.error("Please enable microphone permission.");
                            return;
                          }
                          setMicOn((v) => {
                            const next = !v;
                            setTrackEnabled(streamRef.current, "audio", next);
                            setLocalMicOn(next);
                            return next;
                          });
                        }}
                        aria-pressed={micOn}
                        aria-label={
                          micOn ? "Turn microphone off" : "Turn microphone on"
                        }
                        className={`flex h-10 w-12 items-center justify-center rounded-l-full transition ${
                          micOn
                            ? "bg-blue-700 text-white hover:bg-blue-800"
                            : "bg-red-600 text-white hover:bg-red-700"
                        }`}
                      >
                        {micOn ? (
                          <Mic className="h-5 w-5" />
                        ) : (
                          <MicOff className="h-5 w-5" />
                        )}
                      </button>
                      <DevicePicker
                        kind="microphone"
                        selectedId={microphoneId}
                        onSelect={selectMicrophone}
                        variant="segmented"
                      />
                    </div>

                    <div className="flex items-center">
                      <button
                        type="button"
                        onClick={() => {
                          if (speakerOn) {
                            setSpeakerOn(false);
                            return;
                          }

                          setSpeakerOn(true);
                        }}
                        onDoubleClick={handleSpeakerTest}
                        disabled={speakerTesting}
                        aria-pressed={speakerOn}
                        aria-label={
                          speakerOn ? "Turn speaker off" : "Turn speaker on"
                        }
                        title={
                          speakerOn
                            ? "Turn speaker off. Double-click to test."
                            : "Turn speaker on"
                        }
                        className={`flex h-10 w-12 items-center justify-center rounded-l-full transition ${
                          !speakerOn
                            ? "bg-red-600 text-white hover:bg-red-700"
                            : speakerTesting
                              ? "cursor-not-allowed bg-emerald-600 text-white"
                              : "bg-blue-700 text-white hover:bg-blue-800"
                        }`}
                      >
                        {speakerOn ? (
                          <Volume2 className="h-5 w-5" />
                        ) : (
                          <VolumeX className="h-5 w-5" />
                        )}
                      </button>
                      <DevicePicker
                        kind="speaker"
                        selectedId={speakerId}
                        onSelect={selectSpeaker}
                        variant="segmented"
                      />
                    </div>

                    <div className="flex items-center">
                      <button
                        type="button"
                        onClick={() => {
                          if (!hasCameraPermission) {
                            toast.error("Please enable camera permission.");
                            return;
                          }
                          if (camOn) {
                            toast.success(
                              "You can't turn off your camera in a meeting.",
                            );
                          }
                          setCamOn(true);
                        }}
                        aria-pressed={camOn}
                        aria-label={
                          camOn ? "Turn camera off" : "Turn camera on"
                        }
                        className={`flex h-10 w-12 items-center justify-center rounded-l-full transition ${
                          camOn
                            ? "bg-blue-700 text-white hover:bg-blue-800"
                            : "bg-red-600 text-white hover:bg-red-700"
                        }`}
                      >
                        {camOn ? (
                          <Video className="h-5 w-5" />
                        ) : (
                          <VideoOff className="h-5 w-5" />
                        )}
                      </button>
                      <DevicePicker
                        kind="camera"
                        selectedId={cameraId}
                        onSelect={selectCamera}
                        variant="segmented"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {mediaError && (
                <p className="mt-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-600">
                  {mediaError}
                </p>
              )}
            </div>

            <div className="flex flex-col gap-5">
              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-xl">
                <h2 className="mb-4 flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-slate-500">
                  <Scale className="h-4 w-4" /> Case Information
                </h2>

                <dl className="space-y-3">
                  <InfoRow
                    icon={<Users className="h-4 w-4" />}
                    label="Bench"
                    value={caseInfo.benchName}
                  />
                  <InfoRow
                    icon={<DoorClosed className="h-4 w-4" />}
                    label="Court Room"
                    value={caseInfo.courtRoom}
                  />
                  <InfoRow
                    icon={<Hash className="h-4 w-4" />}
                    label="Case Number"
                    value={caseInfo.caseNumber}
                  />
                  <InfoRow
                    icon={<Gavel className="h-4 w-4" />}
                    label="Judges"
                    value={caseInfo?.judges
                      ?.map((judge: any) => judge.name)
                      .join(" | ")}
                  />
                  <InfoRow
                    icon={<CalendarDays className="h-4 w-4" />}
                    label="Hearing"
                    value={caseInfo.hearingDate}
                  />
                </dl>

                {(caseInfo.caseType || caseInfo.caseStage) && (
                  <div className="mt-4 flex flex-wrap gap-2">
                    {caseInfo.caseType && <Tag>{caseInfo.caseType}</Tag>}
                    {caseInfo.caseStage && <Tag>{caseInfo.caseStage}</Tag>}
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between gap-3">
                <Button
                  fullWidth
                  type="button"
                  variant="danger"
                  onClick={handleCancel}
                >
                  Cancel
                </Button>
                <Button
                  fullWidth
                  type="button"
                  variant="success"
                  onClick={handleJoin}
                >
                  Join now
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrejoinMeeting;

const InfoRow = ({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value?: string;
}) => {
  if (!value) return null;
  return (
    <div className="flex gap-3">
      <span className="mt-0.5 text-slate-400">{icon}</span>
      <div className="min-w-0">
        <dt className="text-xs font-medium uppercase tracking-wide text-slate-400">
          {label}
        </dt>
        <dd className="text-sm font-semibold text-slate-900">{value}</dd>
      </div>
    </div>
  );
};

const Tag = ({ children }: { children: React.ReactNode }) => (
  <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
    {children}
  </span>
);
